import React, { useState, useEffect, useRef, useCallback } from "react";

const AnimatedSearchPlaceholder = ({ suggestions = [], isFocused, searchValue }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationState, setAnimationState] = useState("visible"); // "visible" | "exiting" | "entering"
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const DISPLAY_DURATION = 2000; // ms between transitions
  const EXIT_DURATION = 400;    // ms for slide-out animation
  const ENTER_DURATION = 400;   // ms for slide-in animation

  // Determine if placeholder should be visible
  const hasValue = searchValue && searchValue.length > 0;
  const shouldShow = !hasValue;
  const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;

  // Reset animation when suggestions change
  useEffect(() => {
    setCurrentIndex(0);
    setAnimationState("visible");
  }, [suggestions]);

  // Main animation loop
  const startCycle = useCallback(() => {
    // Clear any existing timers
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!hasSuggestions || suggestions.length <= 1) return;

    intervalRef.current = setInterval(() => {
      // Phase 1: Exit current text (slide up + fade out)
      setAnimationState("exiting");

      // Phase 2: After exit completes, switch text and enter (slide in from below + fade in)
      timeoutRef.current = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % suggestions.length);
        setAnimationState("entering");

        // Phase 3: After enter completes, settle to visible
        timeoutRef.current = setTimeout(() => {
          setAnimationState("visible");
        }, ENTER_DURATION);
      }, EXIT_DURATION);
    }, DISPLAY_DURATION);
  }, [hasSuggestions, suggestions]);

  useEffect(() => {
    if (shouldShow && hasSuggestions && suggestions.length > 1) {
      startCycle();
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [shouldShow, startCycle, suggestions]);

  // Reset to beginning when user clears input
  useEffect(() => {
    if (shouldShow && hasSuggestions) {
      setCurrentIndex(0);
      setAnimationState("visible");
    }
  }, [shouldShow]);

  if (!shouldShow) return null;

  // Fallback when no suggestions
  if (!hasSuggestions) {
    return (
      <div className="animated-placeholder">
        <span className="placeholder-static-text">Search products...</span>
      </div>
    );
  }

  const currentSuggestion = suggestions[currentIndex] || "";

  // Determine CSS class for the sliding text
  let sliderClass = "placeholder-slider-item";
  if (animationState === "exiting") {
    sliderClass += " placeholder-exit";
  } else if (animationState === "entering") {
    sliderClass += " placeholder-enter";
  } else {
    sliderClass += " placeholder-idle";
  }

  return (
    <div className="animated-placeholder">
      <div className="placeholder-slider-wrapper">
        <span className={sliderClass} key={currentIndex}>
          Search "{currentSuggestion}"
        </span>
      </div>
    </div>
  );
};

export default AnimatedSearchPlaceholder;
